/**
 * O cartão de indicador do Fechamento.
 *
 * Papel: reproduz `.kpi-card`, `.kpi-label` e `.kpi-value` (l. 64–73) —
 * `16px 18px`, borda de cima de 3 px na cor do indicador, rótulo em mono 10 px
 * caixa alta e valor em Outfit 26 px 300.
 *
 * Como usar: `indicador` escolhe a cor da borda de cima (`inicial` cinza,
 * `entrada` verde, `saida` vermelha, `final` latão) e `corDoValor` a do número,
 * que a origem decide à parte — o Saldo Final negativo fica vermelho, o Saldo
 * Inicial negativo não. Enquanto o Fechamento carrega, `valor` é nulo e o
 * cartão mostra só o rótulo, como o `<div>` vazio da origem antes do primeiro
 * desenho.
 */
import { cn } from '@/shared/lib/utils';

/** Os quatro indicadores do Fechamento. */
export type IndicadorDoKpi = 'inicial' | 'entrada' | 'saida' | 'final';

/** A cor da borda de cima de cada indicador (`.k-ini`, `.k-rec`, `.k-desp`, `.k-fin`). */
const BORDA_DO_INDICADOR: Readonly<Record<IndicadorDoKpi, string>> = {
  inicial: 'border-t-texto-fraco',
  entrada: 'border-t-sucesso',
  saida: 'border-t-perigo',
  final: 'border-t-brass',
};

/** As três cores de valor da origem: o `.kpi-value` puro, `.green` e `.red`. */
export type CorDoValorDoKpi = 'normal' | 'sucesso' | 'perigo';

/** A classe de cada cor de valor. */
const COR_DO_VALOR: Readonly<Record<CorDoValorDoKpi, string>> = {
  normal: 'text-foreground',
  sucesso: 'text-sucesso',
  perigo: 'text-perigo',
};

interface CartaoKpiProps {
  /** O texto de cima, como a origem o escreve; a caixa alta vem do CSS. */
  rotulo: string;
  indicador: IndicadorDoKpi;
  /** O valor já escrito, ou nulo enquanto carrega. */
  valor: string | null;
  corDoValor: CorDoValorDoKpi;
  /** Classes de quem chama, como o respiro menor da impressão. */
  className?: string;
}

/**
 * Um indicador do Fechamento.
 *
 * @param rotulo - "Saídas", "Entradas", "Saldo Inicial" ou "Saldo Final".
 * @param indicador - Qual dos quatro, pela cor da borda de cima.
 * @param valor - O número já escrito por `formatarReais`, ou nulo.
 * @param corDoValor - A cor do número.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O cartão.
 */
export function CartaoKpi({
  rotulo,
  indicador,
  valor,
  corDoValor,
  className,
}: CartaoKpiProps) {
  return (
    <div
      className={cn(
        'rounded-emb border border-t-[3px] border-emb-borda bg-superficie px-[18px] py-4',
        BORDA_DO_INDICADOR[indicador],
        className,
      )}
    >
      <div className="mb-1.5 font-mono text-[10px] tracking-[0.7px] text-texto-fraco uppercase">
        {rotulo}
      </div>
      <div
        className={cn(
          'font-outfit text-[26px] font-light tracking-[-0.3px]',
          COR_DO_VALOR[corDoValor],
        )}
      >
        {valor}
      </div>
    </div>
  );
}
